/**
 * Wariant tylko, gdy realizacja mieści się w oknach zlecenia (21.10.2026,
 * S-21.10-22, przełącznik `variantWithinWindows`; reguła w API:
 * `apps/api/src/user-offers/variant-window.ts`).
 *
 * - Wiersz rekomendacji (oba kierunki) niesie `variantRefusal`, gdy
 *   realizacja pewnie nie mieści się w oknach — przycisk „Wariant” (i „Obsadź”,
 *   które wariant zakłada) jest wtedy wyszarzony z tym powodem w dymku. API
 *   i tak odmawia (409) — wyszarzony przycisk nie jest regułą.
 * - Odpowiedź „Dodaj wariant” niesie `windowWarning`, gdy wynik jest
 *   niepewny albo brak danych — dopisujemy je do komunikatu.
 * - Od 22.10.2026 (decyzje użytkownika pkt 3 i 4): odmowa i ostrzeżenia
 *   podają spóźnienie dwiema liczbami z opisem źródła (`variantLateness`,
 *   `lateness` w planie pojazdu), a obsadzenie autem pewnie poza oknami
 *   („Obsadź” na karcie, „Obsadź…” w wierszu banku) ostrzega, nie blokuje —
 *   odpowiedź `assign-car` niesie wtedy `windowWarning`.
 */

/** Ostrzeżenie o oknach z odpowiedzi „Dodaj wariant”; brak = `null`. */
export function windowWarningOf(response: unknown): string | null {
  const w =
    response && typeof response === 'object' && 'windowWarning' in response
      ? (response as { windowWarning?: unknown }).windowWarning
      : null;
  return typeof w === 'string' && w.trim() !== '' ? w.trim() : null;
}

/** Komunikat po dodaniu wariantu z ostrzeżeniem o oknach, gdy jest. */
export function withWindowWarning(message: string, response: unknown): string {
  const warning = windowWarningOf(response);
  if (warning === null) return message;
  return /[.!?]$/.test(message) ? `${message} ${warning}` : `${message}. ${warning}`;
}

/** Powód wyszarzenia „Wariant” / „Obsadź” z wiersza; brak = wolno. */
export function variantRefusalOf(row: { variantRefusal?: string | null }): string | null {
  return typeof row.variantRefusal === 'string' && row.variantRefusal.trim() !== ''
    ? row.variantRefusal
    : null;
}

/**
 * Dwie liczby spóźnienia z opisem źródła (22.10.2026, decyzja użytkownika
 * pkt 4) — kształt jak `LatenessFigures` w API (`lateness-figures.ts`):
 * „wg bieżącej pozycji i czasu pracy kierowcy (z ulgami)” i „wg planu (start
 * w pierwszym możliwym terminie, bez ulg)”; `hoursAfterWindow: null` = brak
 * danych (powód w zdaniu). Od 23.10.2026 obie mierzą koniec rozładunku.
 */
export interface LatenessFigure {
  hoursAfterWindow: number | null;
  text: string;
}
export interface LatenessFigures {
  current: LatenessFigure;
  plan: LatenessFigure;
}

function isFigure(v: unknown): v is LatenessFigure {
  return !!v && typeof v === 'object' && typeof (v as { text?: unknown }).text === 'string';
}

/** Obie liczby z odpowiedzi API (`variantLateness`, `lateness`); brak albo uszkodzone = `null`. */
export function latenessOf(value: unknown): LatenessFigures | null {
  if (!value || typeof value !== 'object') return null;
  const { current, plan } = value as { current?: unknown; plan?: unknown };
  return isFigure(current) && isFigure(plan) ? { current, plan } : null;
}

/**
 * Nagłówek obu liczb — co mierzą (23.10.2026, decyzja użytkownika: obie
 * to KONIEC rozładunku wobec końca okna rozładunku). Ten sam tekst co
 * `LATENESS_HEADING` w API (`lateness-figures.ts`).
 */
export const LATENESS_HEADING = 'Spóźnienie końca rozładunku wobec końca okna rozładunku';

/** Zdanie z wielką literą na początku — do linijek dymku i okna. */
const sentence = (t: string) => (t ? `${t.charAt(0).toUpperCase()}${t.slice(1)}` : t);

/** Linijki „Spóźnienie” — najpierw pozycja i zegar kierowcy, potem plan. */
export function latenessLines(l: LatenessFigures): string[] {
  return [sentence(l.current.text), sentence(l.plan.text)];
}

/**
 * Obsadzenie mimo pewnego braku czasu (22.10.2026, decyzja użytkownika
 * pkt 3): „Obsadź” na karcie zlecenia i „Obsadź…” w wierszu banku zostają
 * furtką Team Leadera — okno ostrzega, ale nie blokuje, a powód nie jest
 * wymagany. Dziennik zlecenia zapisze „obsadzono mimo braku czasu”.
 */
export const LATE_ASSIGNMENT_WARNING =
  'Realizacja nie mieści się w oknach zlecenia nawet z pełną pulą ulg (czas jazdy kierowcy wg telematyki). ' +
  'Możesz obsadzić — powód nie jest wymagany, a w historii zlecenia zapisze się „obsadzono mimo braku czasu” z liczbami.';

/**
 * Ostrzeżenie przy obsadzeniu autem z wiersza rekomendacji (`variantRefusal`
 * = ta sama pewność co odmowa wariantu); `null` = realizacja nie jest pewnie
 * poza oknami.
 */
export function lateAssignmentWarningOf(
  row: { variantRefusal?: string | null; variantLateness?: unknown } | null | undefined,
): { text: string; lines: string[] } | null {
  if (!row || variantRefusalOf(row) === null) return null;
  const l = latenessOf(row.variantLateness);
  return { text: LATE_ASSIGNMENT_WARNING, lines: l ? latenessLines(l) : [] };
}
